import type { PaymentMilestoneData } from '@/lib/api'
import { isPaymentMilestoneEditable } from '@/lib/payment-milestone-editability'
import {
  getMilestoneReplacement,
  getReservedMilestones,
  type MilestoneReplacement,
} from '@/lib/payment-milestone-replacement'
import { PAYLINK_STATUS, PaymentMilestoneStatus } from '@/lib/types'
import Decimal from 'decimal.js'

export type DisplayPaymentMilestone = PaymentMilestoneData & {
  isCurrentChangeOrderMilestone: boolean
  replacement: MilestoneReplacement | null
}

function toDisplayMilestone(
  milestone: PaymentMilestoneData,
  quoteId: string,
  isCurrentChangeOrderMilestone: boolean
): DisplayPaymentMilestone {
  return { ...milestone, isCurrentChangeOrderMilestone, replacement: getMilestoneReplacement(milestone, quoteId) }
}

export function includePersistedAmountOnlyMilestones(
  currentMilestones: PaymentMilestoneData[],
  persistedMilestones: PaymentMilestoneData[]
) {
  const currentIds = new Set(currentMilestones.map((milestone) => milestone.dsy_paymentmilestoneid))

  return [
    ...currentMilestones,
    ...persistedMilestones.filter(
      (milestone) =>
        !isPaymentMilestoneEditable(milestone) && !currentIds.has(milestone.dsy_paymentmilestoneid)
    ),
  ].sort((a, b) => a.dsy_sequencenumber - b.dsy_sequencenumber)
}

export function buildPaymentMilestoneSchedule({
  currentMilestones,
  salesOrderMilestones,
  quoteId,
  includeSalesOrderHistory,
}: {
  currentMilestones: PaymentMilestoneData[]
  salesOrderMilestones: PaymentMilestoneData[]
  quoteId: string
  includeSalesOrderHistory: boolean
}): DisplayPaymentMilestone[] {
  const currentIds = new Set(currentMilestones.map((milestone) => milestone.dsy_paymentmilestoneid))
  if (!includeSalesOrderHistory) {
    // Rows this change order replaces are part of what the customer signs,
    // so they show even when payment history is hidden.
    const reserved = getReservedMilestones(salesOrderMilestones, quoteId)
      .filter((milestone) => !currentIds.has(milestone.dsy_paymentmilestoneid))
      .sort((a, b) => a.dsy_sequencenumber - b.dsy_sequencenumber)
    return orderReplacementRows(
      [...reserved, ...currentMilestones].map((milestone) => toDisplayMilestone(milestone, quoteId, false)),
      quoteId
    )
  }

  const historical = new Map<string, DisplayPaymentMilestone>()
  const currentFromSalesOrder = new Map<string, DisplayPaymentMilestone>()

  salesOrderMilestones.forEach((milestone) => {
    if (currentIds.has(milestone.dsy_paymentmilestoneid)) return
    const row = toDisplayMilestone(milestone, quoteId, milestone._dsy_quote_value === quoteId)
    if (row.isCurrentChangeOrderMilestone) {
      currentFromSalesOrder.set(milestone.dsy_paymentmilestoneid, row)
    } else {
      historical.set(milestone.dsy_paymentmilestoneid, row)
    }
  })

  const history = Array.from(historical.values()).sort((a, b) => a.dsy_sequencenumber - b.dsy_sequencenumber)
  const ownRows = [
    ...Array.from(currentFromSalesOrder.values()).sort((a, b) => a.dsy_sequencenumber - b.dsy_sequencenumber),
    ...currentMilestones
      .map((milestone) => toDisplayMilestone(milestone, quoteId, true))
      .sort((a, b) => a.dsy_sequencenumber - b.dsy_sequencenumber),
  ]
  return orderReplacementRows([...history, ...ownRows], quoteId)
}

// Display only (sequence numbers untouched): a change order's rows take the
// place of the rows it replaced, so list them right after the last of those
// rows to be listed — for every replacing quote shown, nested ones too.
// Everything else keeps its order.
function orderReplacementRows(rows: DisplayPaymentMilestone[], quoteId: string) {
  const ownerOf = (row: DisplayPaymentMilestone) =>
    row.isCurrentChangeOrderMilestone || row._dsy_quote_value === quoteId ? quoteId : row._dsy_quote_value
  const replacerOf = (row: DisplayPaymentMilestone) =>
    row.replacement ? (row.replacement.kind === 'self' ? quoteId : row.replacement.quoteId) : null

  // Replaced rows still to be listed, per replacing quote. A replaced row can
  // itself sit in another quote's group, so "last" is decided while listing.
  const unlisted = new Map<string, number>()
  rows.forEach((row) => {
    const replacer = replacerOf(row)
    if (replacer) unlisted.set(replacer, (unlisted.get(replacer) ?? 0) + 1)
  })
  if (unlisted.size === 0) return rows

  const groups = new Map<string, DisplayPaymentMilestone[]>()
  rows.forEach((row) => {
    const owner = ownerOf(row)
    if (owner && unlisted.has(owner)) groups.set(owner, [...(groups.get(owner) ?? []), row])
  })

  const ordered: DisplayPaymentMilestone[] = []
  const emitted = new Set<DisplayPaymentMilestone>()
  const emit = (row: DisplayPaymentMilestone) => {
    if (emitted.has(row)) return
    emitted.add(row)
    ordered.push(row)
    const replacer = replacerOf(row)
    if (!replacer) return
    const left = (unlisted.get(replacer) ?? 0) - 1
    unlisted.set(replacer, left)
    if (left === 0) groups.get(replacer)?.forEach(emit)
  }
  rows.forEach((row) => {
    const owner = ownerOf(row)
    if (!owner || !groups.has(owner)) emit(row)
  })
  // Safety net: a group whose anchor never surfaced keeps its place at the end
  rows.forEach(emit)
  return ordered
}

/** A change order's own rows are percents of its pool, not of the project —
 * hide that share once the schedule also lists rows from other quotes
 * (sales-order history or rows it replaces). */
export function shouldHideOwnMilestoneShare(milestones: DisplayPaymentMilestone[], quoteId: string) {
  return milestones.some((milestone) => milestone._dsy_quote_value !== quoteId)
}

// Derived (not raw statuscode) so a lagging Open statuscode with a
// paid/waiting paylink still surfaces its badge alongside the stats block.
export function shouldShowStatus(milestones: DisplayPaymentMilestone[]) {
  return milestones.some((milestone) => getMilestoneDisplayStatus(milestone) !== 'upcoming')
}

export type MilestoneDisplayStatus = 'paid' | 'due' | 'toBeInvoiced' | 'upcoming' | 'inactive' | 'replaced'

export const MILESTONE_DISPLAY_STATUS_LABELS: Record<MilestoneDisplayStatus, string> = {
  paid: 'Paid',
  due: 'Due now',
  toBeInvoiced: 'Invoicing soon',
  upcoming: 'Upcoming',
  inactive: 'Inactive',
  replaced: 'Replaced',
}

/** Status for display/rollup/actions. `replaced` behaves like `inactive`. */
export function isMilestoneWithdrawn(status: MilestoneDisplayStatus) {
  return status === 'inactive' || status === 'replaced'
}

export function isMilestonePayable(milestone: PaymentMilestoneData) {
  // A deactivated or already-paid milestone is never payable, even when a
  // stale Waiting-For-Payment paylink is still attached to the row.
  return (
    milestone.statuscode !== PaymentMilestoneStatus.Inactive &&
    milestone.statuscode !== PaymentMilestoneStatus.Paid &&
    !!milestone.dsy_paylink?.nodus_pflink &&
    milestone.dsy_paylink.nodus_pfstatustext === PAYLINK_STATUS.WaitingForPayment
  )
}

// A settled milestone's money is history (paid) or locked into an issued
// invoice/paylink (due) — display must show the persisted amount, not a
// percent recompute against the current quote total.
export function isMilestoneSettled(milestone: PaymentMilestoneData) {
  const status = getMilestoneDisplayStatus(milestone)
  return status === 'paid' || status === 'due'
}

// Receipt access: a paid row keeps its paylink as a receipt — keyed on the
// derived status (not the paylink's own status text) so the anchor survives
// the window where the milestone statuscode is Paid but the Nodus write-back
// to the paylink still lags. An unpaid link on a non-paid row (e.g.
// deactivated milestone) is a live payment URL and must not be exposed.
export function hasPaylinkReceipt(milestone: PaymentMilestoneData) {
  return getMilestoneDisplayStatus(milestone) === 'paid' && !!milestone.dsy_paylink?.nodus_pflink
}

// The paylink status can lead the milestone statuscode while the Nodus
// write-back syncs, so a paid/waiting paylink upgrades the derived status —
// except for Inactive, where the deactivation is authoritative over any
// stale paylink still attached to the row. A replaced row (annotated by
// buildPaymentMilestoneSchedule) shows as replaced — except a row the viewed
// change order reserved that is actually paid or due: real money outranks a
// reservation (the dialog tells staff to drop it).
export function getMilestoneDisplayStatus(
  milestone: PaymentMilestoneData & { replacement?: MilestoneReplacement | null }
): MilestoneDisplayStatus {
  const status = getRecordedMilestoneStatus(milestone)
  if (!milestone.replacement) return status
  if (milestone.replacement.kind === 'self' && (status === 'paid' || status === 'due')) return status
  return 'replaced'
}

function getRecordedMilestoneStatus(milestone: PaymentMilestoneData): MilestoneDisplayStatus {
  if (milestone.statuscode === PaymentMilestoneStatus.Inactive) return 'inactive'
  if (milestone.statuscode === PaymentMilestoneStatus.Paid) return 'paid'
  if (milestone.dsy_paylink?.nodus_pfstatustext === PAYLINK_STATUS.Paid) return 'paid'
  if (milestone.statuscode === PaymentMilestoneStatus.WaitingForPayment || isMilestonePayable(milestone)) return 'due'
  if (milestone.statuscode === PaymentMilestoneStatus.ToBeInvoiced) return 'toBeInvoiced'
  return 'upcoming'
}

export type PayableAction =
  | { kind: 'none' }
  | { kind: 'single'; milestone: DisplayPaymentMilestone; hasLink: boolean }
  | { kind: 'aggregate'; milestoneIds: string[]; amount: number }

// One primary payment action per schedule: a lone due milestone keeps its
// row-level action; two or more roll up into a single pay-everything link
// covering every due milestone (credits net against the total).
// The due predicate and netting are mirrored server-side in proposal-api's
// functions/create_paylink_for_milestones.py (select_due_milestones /
// net_amount) — keep the two in sync.
export function getPayableAction(milestones: DisplayPaymentMilestone[]): PayableAction {
  const due = milestones.filter((milestone) => getMilestoneDisplayStatus(milestone) === 'due')
  if (due.length === 0) return { kind: 'none' }
  // Nothing to collect when the due amounts net to zero or below (credits)
  const amount = due.reduce((sum, milestone) => sum.add(milestone.dsy_amount), new Decimal(0))
  if (amount.lessThanOrEqualTo(0)) return { kind: 'none' }
  if (due.length === 1) return { kind: 'single', milestone: due[0], hasLink: isMilestonePayable(due[0]) }
  return {
    kind: 'aggregate',
    milestoneIds: due.map((milestone) => milestone.dsy_paymentmilestoneid),
    amount: amount.toDecimalPlaces(2).toNumber(),
  }
}

export type PaymentScheduleRollup = {
  total: number
  paid: number
  due: number
  remaining: number
  dueCount: number
  milestoneCount: number
  hasActivity: boolean
  /** Clamped to [0, 100] — safe for CSS widths even with negative amounts. */
  paidPercent: number
  /** Clamped so paidPercent + duePercent never exceeds 100. */
  duePercent: number
  /** Rows an applied change order replaced: their amount now lives there, so
   * paid + due + remaining + moved = the signed total. */
  moved: number
  movedTo: { quoteId: string; label: string }[]
  /** Clamped so the three segments never exceed 100. */
  movedPercent: number
}

export function getPaymentScheduleRollup(
  milestones: (PaymentMilestoneData & { replacement?: MilestoneReplacement | null })[]
): PaymentScheduleRollup {
  let total = new Decimal(0)
  let paid = new Decimal(0)
  let due = new Decimal(0)
  let paidCount = 0
  let dueCount = 0
  let milestoneCount = 0
  let moved = new Decimal(0)
  const movedTo = new Map<string, string>()

  // Quotes whose rows this schedule lists (a later change order with history
  // lists the replacing change order's new rows itself).
  const listedQuoteIds = new Set(milestones.map((milestone) => milestone._dsy_quote_value))

  for (const milestone of milestones) {
    const status = getMilestoneDisplayStatus(milestone)
    // "Moved" only when the replacement rows are elsewhere; when they are
    // listed here, counting both would double the amount.
    if (
      status === 'replaced' &&
      milestone.replacement?.kind === 'other' &&
      !listedQuoteIds.has(milestone.replacement.quoteId)
    ) {
      moved = moved.add(milestone.dsy_amount)
      movedTo.set(milestone.replacement.quoteId, milestone.replacement.label)
      continue
    }
    if (isMilestoneWithdrawn(status)) continue
    milestoneCount += 1
    const amount = new Decimal(milestone.dsy_amount)
    total = total.add(amount)
    if (status === 'paid') {
      paid = paid.add(amount)
      paidCount += 1
    }
    if (status === 'due') {
      due = due.add(amount)
      dueCount += 1
    }
  }

  const remaining = total.minus(paid).minus(due)
  // Percent segments only feed the visual bar; clamping keeps widths valid when
  // amounts are negative (change-order credits) or the total is non-positive.
  const barTotal = total.add(moved)
  const segmentPercent = (part: Decimal) =>
    barTotal.greaterThan(0) ? Decimal.min(Decimal.max(part.div(barTotal).mul(100), 0), 100).toNumber() : 0
  const paidPercent = segmentPercent(paid)
  const duePercent = Math.min(segmentPercent(due), 100 - paidPercent)
  const movedPercent = Math.min(segmentPercent(moved), 100 - paidPercent - duePercent)

  return {
    total: total.toDecimalPlaces(2).toNumber(),
    paid: paid.toDecimalPlaces(2).toNumber(),
    due: due.toDecimalPlaces(2).toNumber(),
    remaining: remaining.toDecimalPlaces(2).toNumber(),
    dueCount,
    milestoneCount,
    // Counts, not sums — paid amounts can net to zero (paid credit offsetting
    // a paid charge) while the schedule still has real activity to show.
    hasActivity: paidCount > 0 || dueCount > 0,
    paidPercent,
    duePercent,
    moved: moved.toDecimalPlaces(2).toNumber(),
    movedTo: Array.from(movedTo, ([quoteId, label]) => ({ quoteId, label })),
    movedPercent,
  }
}

export type PaymentScheduleChange = {
  replaced: number
  added: number
  net: number
}

/** "Payment schedule change" reconciliation for a change order that replaces
 * milestones: −replaced, +new, net = the change order total. Null when the
 * change order replaces nothing, or while a row it reserved is really paid or
 * due (the lines would no longer net to the change order; the dialog tells
 * staff to drop that reservation). */
export function getPaymentScheduleChange(
  milestones: DisplayPaymentMilestone[],
  quoteId: string
): PaymentScheduleChange | null {
  let replaced = new Decimal(0)
  let added = new Decimal(0)
  let hasReplaced = false
  for (const milestone of milestones) {
    const status = getMilestoneDisplayStatus(milestone)
    if (milestone.replacement?.kind === 'self' && status !== 'replaced') return null
    if (milestone.replacement?.kind === 'self') {
      hasReplaced = true
      replaced = replaced.add(milestone.dsy_amount)
    } else if (milestone._dsy_quote_value === quoteId && !isMilestoneWithdrawn(status)) {
      added = added.add(milestone.dsy_amount)
    }
  }
  if (!hasReplaced) return null
  return {
    replaced: replaced.toDecimalPlaces(2).toNumber(),
    added: added.toDecimalPlaces(2).toNumber(),
    net: added.minus(replaced).toDecimalPlaces(2).toNumber(),
  }
}

export type ScheduleChangeLine = { label: string; amount: number | null }

/** Customer wording for the reconciliation: what was replaced, what is new,
 * and the net effect on their total. */
export function getScheduleChangeLines(change: PaymentScheduleChange): ScheduleChangeLine[] {
  return [
    { label: 'Payments replaced', amount: -change.replaced },
    { label: 'New payments', amount: change.added },
    change.net === 0 ? { label: 'No change to your total', amount: null } : { label: 'Net change', amount: change.net },
  ]
}

/** "Total project value" on a change order: the sales-order total once this
 * change order is applied, so every row it reserved is left out — including a
 * reserved row that is (wrongly) paid or due, which still counts in the
 * paid/due stats. */
export function getProjectValueTotal(milestones: DisplayPaymentMilestone[]) {
  return milestones
    .filter(
      (milestone) =>
        milestone.replacement?.kind !== 'self' && !isMilestoneWithdrawn(getMilestoneDisplayStatus(milestone))
    )
    .reduce((sum, milestone) => sum.add(milestone.dsy_amount), new Decimal(0))
    .toDecimalPlaces(2)
    .toNumber()
}

export function formatEstimatedInvoiceDate(value: string) {
  const datePart = value.slice(0, 10)
  const date = new Date(`${datePart}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}
